import styled from 'styled-components';
import React, { ReactNode } from 'react';

import { LayoutColors } from '@src/core/theme';
import { device } from '@src/common/breakpoints';
import { formatDate, formatMinutes } from '@src/common';
import StepNavigationButton from './StepNavigationButton';
import { BodyLarge, BodyRegular } from '@src/fonts/typography/Typography';
import { ScheduleTime } from '@src/core/buyerApiClient';
import { useWidgetsTheme as theme, useTranslate } from '@src/core/hooks';

type Props = {
  time?: ScheduleTime;
  isLastStep: boolean;
  selectedDate: string;
  onNextStep: () => void;
  onPreviousStep: () => void;
  isWithDateAndTime: boolean;
  children: ReactNode;
};

const StepSummary = (props: Props) => {
  const { children, selectedDate, onPreviousStep, onNextStep, isLastStep, isWithDateAndTime, time } = props;

  const { i18n } = useTranslate();
  const actionColor = theme('actionColor');

  return (
    <>
      <Summary>
        <SummaryItem>
          <h4>{i18n('reschedule_selected_date')}</h4>
          <span>{formatDate(selectedDate!)}</span>
        </SummaryItem>
        {time ? (
          <SummaryItem>
            <h4>{i18n('reschedule_selected_time')}</h4>
            <span>{formatMinutes(time.minutes)}</span>
          </SummaryItem>
        ) : null}
      </Summary>
      {children}
      <ButtonArea>
        <StepNavigationButton onClick={onPreviousStep} style={{ borderColor: actionColor, color: actionColor }}>
          {i18n('reschedule_go_back_button')}
        </StepNavigationButton>

        <StepNavigationButton
          onClick={onNextStep}
          disabled={isWithDateAndTime && !time}
          style={{ background: actionColor }}
        >
          {isLastStep ? i18n('reschedule_confirm_button') : i18n('reschedule_go_forward_button')}
        </StepNavigationButton>
      </ButtonArea>
    </>
  );
};

const Summary = styled.section`
  display: flex;
  flex-direction: row;
  gap: 24px;
  align-items: flex-start;
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  color: ${LayoutColors.blackDark};
  margin-bottom: 24px;

  h4 {
    ${BodyLarge};
    color: inherit;
  }

  span {
    ${BodyRegular};
    color: inherit;
  }
`;

const ButtonArea = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  margin-top: 24px;
  ${device.smallTablet} {
    flex-direction: row;
    align-items: center;
    button {
      width: 205px;
    }
  }
`;

export default StepSummary;
